import React from 'react';
import { Instagram, Heart, Play } from 'lucide-react';

// Real files already in /app/backend/uploads — using our own dishes so
// the mockups feel accurate to the brand.
const POSTS = [
  { img: '/api/upload/images/243f9ac5-815d-4265-9bbd-b874d31b1844.gif', caption: 'The full spread — 8 dishes to share', ago: '2h', likes: 483 },
  { img: '/api/upload/images/a8803eb1-7bf4-4fc9-be7e-cbff5088df77.gif', caption: 'Kachori Sunday, all day', ago: '5h', likes: 291 },
  { img: '/api/upload/images/ede34a7d951a4a64b91051cebd24ae0c.gif', caption: 'Laal Maas straight from the tandoor', ago: '1d', likes: 612 },
  { img: '/api/upload/images/3210a0828af64475a2dc27593543d0b2.gif', caption: 'Banarasi Aloo — plated', ago: '2d', likes: 401 },
  { img: '/api/upload/images/f4050ab5ef674104938ec16b8ba263cf.gif', caption: 'Jamun cocktail with a curry-leaf twist', ago: '3d', likes: 355 },
  { img: '/api/upload/images/f936cf79e958455eb45523a30c6e1d41.gif', caption: 'Chef Sanjay on the pass', ago: '4d', likes: 528 },
  { img: '/api/upload/images/06112fd1-3f79-4574-9b22-baef9accc477.gif', caption: 'Private event setup', ago: '5d', likes: 214 },
  { img: '/api/upload/images/0bf240685a81444ca74e633fdb99d8c1.jpg', caption: 'The dining room in golden hour', ago: '1w', likes: 302 },
  { img: '/api/upload/images/413cdead763b4865bee9f0716fe7e5f2.jpg', caption: "Chef's table nights", ago: '1w', likes: 447 },
];

const SectionHeader = ({ num, title, subtitle }) => (
  <div className="max-w-7xl mx-auto px-6 pt-16 pb-6 border-t border-dashed border-gray-300">
    <div className="flex items-baseline gap-4">
      <span className="text-6xl font-serif text-[#C9A86C]">{num}</span>
      <div>
        <h2 className="text-3xl font-serif">{title}</h2>
        <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
      </div>
    </div>
  </div>
);

// ---------- Option 1: Editorial Wall ----------
const Option1 = () => (
  <section className="py-20 bg-[#0f0f0e] text-white">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-10">
        <p className="text-[10px] tracking-[0.45em] uppercase text-[#C9A86C] mb-3 font-serif">From The Kitchen</p>
        <h3 className="text-5xl font-serif mb-3">@bricklane</h3>
        <a className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#C9A86C] transition">
          <Instagram size={16} /> Follow on Instagram
        </a>
      </div>
      <div className="grid grid-cols-4 grid-rows-2 gap-2 aspect-[2/1]">
        <a className="relative col-span-2 row-span-2 group overflow-hidden">
          <img src={POSTS[0].img} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="" />
          <div className="absolute inset-0 flex items-end p-6 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
            <div>
              <p className="text-sm">{POSTS[0].caption}</p>
              <p className="text-[11px] text-white/60 mt-1 tracking-wider uppercase">{POSTS[0].ago} ago · ♥ {POSTS[0].likes}</p>
            </div>
          </div>
        </a>
        {POSTS.slice(1, 5).map((p, i) => (
          <a key={i} className="relative group overflow-hidden">
            <img src={p.img} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="" />
            <div className="absolute inset-0 flex items-end p-3 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <p className="text-[10px] text-white/70 tracking-wider uppercase">{p.ago} ago</p>
            </div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Option 2: Marquee Ticker ----------
const Option2 = () => (
  <section className="py-16 bg-[#FBF6EC] overflow-hidden">
    <div className="text-center mb-8 px-6">
      <p className="text-[10px] tracking-[0.45em] uppercase text-[#C9A86C] font-serif mb-2">LIVE · @bricklane</p>
      <h3 className="text-4xl font-serif">Straight from the Arts District</h3>
    </div>
    {/* Simulate scroll with -translate hover pause */}
    <div className="flex gap-3 animate-[marquee_35s_linear_infinite] w-max">
      {[...POSTS, ...POSTS].map((p, i) => (
        <a key={i} className="relative w-64 h-64 flex-shrink-0 overflow-hidden group">
          <img src={p.img} className="w-full h-full object-cover" alt="" />
          <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full">{p.ago}</div>
        </a>
      ))}
    </div>
    <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
  </section>
);

// ---------- Option 3: Video-First Reel Wall ----------
const Option3 = () => (
  <section className="py-20 bg-[#0f0f0e] text-white relative">
    <div className="max-w-7xl mx-auto px-6">
      <div className="mb-10 flex items-end justify-between">
        <div>
          <p className="text-[10px] tracking-[0.45em] uppercase text-[#C9A86C] mb-2 font-serif">Reels · Live from the Pass</p>
          <h3 className="text-5xl font-serif">Watch it happen.</h3>
        </div>
        <a className="text-sm text-white/70 hover:text-[#C9A86C] flex items-center gap-2"><Instagram size={16}/> All Reels</a>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {POSTS.slice(0, 3).map((p, i) => (
          <a key={i} className="relative aspect-[9/16] overflow-hidden group rounded-sm">
            <img src={p.img} className="w-full h-full object-cover" alt="" />
            <div className="absolute inset-0 bg-black/20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center group-hover:bg-white/40 transition-colors">
                <Play size={24} className="text-white ml-1" fill="white" />
              </div>
            </div>
            <div className="absolute bottom-4 left-4 right-4">
              <p className="text-sm mb-1">{p.caption}</p>
              <div className="flex items-center gap-2 text-[10px] text-white/70 tracking-wider uppercase">
                <Heart size={11} fill="white" /> {p.likes} · {p.ago} ago
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Option 4: Split Story View ----------
const Option4 = () => (
  <section className="py-20 bg-[#FFFDF9]">
    <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-12 items-center">
      <div>
        <p className="text-[10px] tracking-[0.45em] uppercase text-[#C9A86C] mb-4 font-serif">@bricklane · Live</p>
        <h3 className="text-5xl font-serif leading-[1.05] mb-6">Live from the Arts District.</h3>
        <p className="text-gray-600 leading-relaxed mb-8">
          Every plate, every service, every night — behind-the-scenes of what Brick Lane is cooking today.
          Follow us on Instagram for daily updates.
        </p>
        <a className="inline-flex items-center gap-3 border border-black px-6 py-3 hover:bg-black hover:text-white transition-colors text-sm tracking-wider uppercase">
          <Instagram size={16}/> Follow @bricklane
        </a>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {POSTS.slice(0, 9).map((p, i) => (
          <a key={i} className="relative aspect-square overflow-hidden group">
            <img src={p.img} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" alt="" />
            <div className="absolute bottom-1 right-1 bg-white/90 text-black text-[9px] px-1.5 py-0.5 rounded">{p.ago}</div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Option 5: Story Card Stack ----------
const Option5 = () => (
  <section className="py-16 bg-[#FBF6EC]">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-8">
        <p className="text-[10px] tracking-[0.45em] uppercase text-[#C9A86C] mb-2 font-serif">Stories</p>
        <h3 className="text-4xl font-serif">@bricklane</h3>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2">
        {POSTS.slice(0, 6).map((p, i) => (
          <a key={i} className="relative flex-shrink-0 w-52 aspect-[9/16] rounded-2xl overflow-hidden group ring-2 ring-[#C9A86C] ring-offset-2">
            <img src={p.img} className="w-full h-full object-cover" alt="" />
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
              <p className="text-white text-xs mb-1">{p.caption.slice(0, 40)}…</p>
              <p className="text-white/70 text-[10px] tracking-wider uppercase">{p.ago} ago</p>
            </div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Page ----------
const InstagramDesignPreview = () => (
  <div className="min-h-screen bg-white">
    <div className="max-w-7xl mx-auto px-6 pt-12 pb-2">
      <p className="text-[10px] tracking-[0.45em] uppercase text-[#C9A86C] mb-2 font-serif">Design Options</p>
      <h1 className="text-4xl font-serif mb-2">Instagram Feed Layouts for Brick Lane</h1>
      <p className="text-gray-500 max-w-2xl">Scroll to compare all 5 designs. All use real Brick Lane images. Every layout supports auto-refresh from Instagram once we connect the integration.</p>
    </div>

    <SectionHeader num="01" title="Editorial Wall" subtitle="Featured hero post + 4 tiles · dark section · brutalist / editorial" />
    <Option1 />

    <SectionHeader num="02" title="Marquee Ticker" subtitle="Full-width auto-scrolling strip · full-bleed · high energy" />
    <Option2 />

    <SectionHeader num="03" title="Video-First Reel Wall" subtitle="3 vertical Reel cards · dark · autoplay muted · maximum liveliness" />
    <Option3 />

    <SectionHeader num="04" title="Split Story View" subtitle="Headline + 9-tile masonry · magazine feel · lots of copy real estate" />
    <Option4 />

    <SectionHeader num="05" title="Story Card Stack" subtitle="Horizontal swipeable story cards · mobile-native · casual" />
    <Option5 />

    <div className="max-w-7xl mx-auto px-6 py-16 border-t border-dashed border-gray-300 mt-8">
      <p className="text-sm text-gray-500">
        Reply with the option number(s) you want to move forward with, plus which integration path (A. Behold.so widget · B. Instagram Graph API direct · C. Curated hybrid).
      </p>
    </div>
  </div>
);

export default InstagramDesignPreview;
